import { create } from 'zustand';
import { drawerKick } from '@sense/shared';
import { isNativeApp, nativeMessage, SdDevices, toBase64 } from './native';

/**
 * Receipt printer connection for this terminal:
 * - system: the browser / Android print dialog (any installed printer, PDF);
 * - bluetooth: Bluetooth LE thermal printers through Web Bluetooth (Chrome on Android, desktop Chrome / Edge);
 * - usb: USB / serial thermal printers through Web Serial (desktop Chrome / Edge);
 * - native-bt: paired Bluetooth (classic SPP) printers from the Android app.
 * The direct kinds send ESC/POS bytes, so they print instantly, cut the paper and can open the cash drawer.
 */
export type PrinterKind = 'system' | 'bluetooth' | 'usb' | 'native-bt';
export interface PrinterConfig { kind: PrinterKind; name?: string; address?: string; usb?: { vendor?: number; product?: number }; drawer: boolean; cut: boolean }

const KEY = 'sd.printer';
const load = (): PrinterConfig => {
  try { return { kind: 'system', drawer: false, cut: true, ...JSON.parse(localStorage.getItem(KEY) ?? '{}') }; } catch { return { kind: 'system', drawer: false, cut: true }; }
};

interface PrinterState { config: PrinterConfig; connected: boolean; busy: boolean; error: string | null }
export const usePrinter = create<PrinterState>(() => ({ config: load(), connected: false, busy: false, error: null }));

export function savePrinter(patch: Partial<PrinterConfig>) {
  const config = { ...usePrinter.getState().config, ...patch };
  try { localStorage.setItem(KEY, JSON.stringify(config)); } catch { /* storage blocked */ }
  usePrinter.setState({ config });
}

/* ---------- Web Bluetooth (BLE) ---------- */
interface BleChar { properties: { write: boolean; writeWithoutResponse: boolean }; writeValueWithoutResponse?(b: BufferSource): Promise<void>; writeValueWithResponse?(b: BufferSource): Promise<void>; writeValue(b: BufferSource): Promise<void> }
interface BleService { getCharacteristics(): Promise<BleChar[]> }
interface BleDevice { id: string; name?: string; gatt?: { connected: boolean; connect(): Promise<{ getPrimaryServices(): Promise<BleService[]> }>; disconnect(): void }; addEventListener(t: 'gattserverdisconnected', fn: () => void): void }
interface BluetoothLike { requestDevice(o: unknown): Promise<BleDevice>; getDevices?(): Promise<BleDevice[]> }
const bluetooth = (): BluetoothLike | undefined => (navigator as unknown as { bluetooth?: BluetoothLike }).bluetooth;

/** Services that common BLE receipt printers (Xprinter, GOOJPRT, MTP-II, Rongta, Epson TM-m30, Zjiang …) expose for writing. */
export const BLE_PRINTER_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb', '0000ffe0-0000-1000-8000-00805f9b34fb', '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000fee7-0000-1000-8000-00805f9b34fb', '0000af30-0000-1000-8000-00805f9b34fb', '0000ae30-0000-1000-8000-00805f9b34fb',
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', '49535343-fe7d-4ae5-8fa9-9fafd205e455', '0000fff0-0000-1000-8000-00805f9b34fb',
];

let ble: { device: BleDevice; ch: BleChar } | null = null;

async function openBle(device: BleDevice) {
  const server = await device.gatt!.connect();
  const services = await server.getPrimaryServices();
  for (const s of services) {
    for (const ch of await s.getCharacteristics().catch(() => [] as BleChar[])) {
      if (ch.properties.writeWithoutResponse || ch.properties.write) {
        ble = { device, ch };
        device.addEventListener('gattserverdisconnected', () => { if (ble?.device === device) { ble = null; usePrinter.setState({ connected: false }); } });
        return;
      }
    }
  }
  device.gatt!.disconnect();
  throw new Error('This Bluetooth device has no printer service. Pick the printer itself.');
}

async function writeBle(bytes: Uint8Array) {
  if (!ble || !ble.device.gatt?.connected) {
    // Chrome remembers allowed devices; reconnect silently when it can.
    const known = (await bluetooth()?.getDevices?.().catch(() => [])) ?? [];
    const d = known.find((x) => x.id === usePrinter.getState().config.address);
    if (!d) throw new Error('The Bluetooth printer is not connected. Connect it again in Settings › Devices.');
    await openBle(d);
  }
  const { ch } = ble!;
  const fast = ch.properties.writeWithoutResponse && ch.writeValueWithoutResponse;
  const size = fast ? 180 : 100; // stays under typical BLE MTUs
  for (let i = 0; i < bytes.length; i += size) {
    const part = bytes.slice(i, i + size);
    if (fast) await ch.writeValueWithoutResponse!(part);
    else if (ch.writeValueWithResponse) await ch.writeValueWithResponse(part);
    else await ch.writeValue(part);
  }
}

/* ---------- Web Serial (USB) ---------- */
interface SerialPortLike { open(o: { baudRate: number }): Promise<void>; close(): Promise<void>; writable: WritableStream<Uint8Array> | null; getInfo?(): { usbVendorId?: number; usbProductId?: number } }
interface SerialLike { requestPort(o?: unknown): Promise<SerialPortLike>; getPorts(): Promise<SerialPortLike[]> }
const serial = (): SerialLike | undefined => (navigator as unknown as { serial?: SerialLike }).serial;
let usbPort: SerialPortLike | null = null;

/** Ports this site may use, matched by USB ids so the scale and the printer don't swap. */
export async function findPort(ids?: { vendor?: number; product?: number }): Promise<SerialPortLike | undefined> {
  const ports = (await serial()?.getPorts().catch(() => [])) ?? [];
  if (!ids?.vendor) return undefined;
  return ports.find((p) => p.getInfo?.().usbVendorId === ids.vendor && p.getInfo?.().usbProductId === ids.product);
}

async function writeUsb(bytes: Uint8Array) {
  if (!usbPort) {
    const p = await findPort(usePrinter.getState().config.usb);
    if (!p) throw new Error('The USB printer is not connected. Connect it again in Settings › Devices.');
    await p.open({ baudRate: 9600 }).catch(() => { /* already open */ });
    usbPort = p;
  }
  const w = usbPort.writable!.getWriter();
  try { await w.write(bytes); } finally { w.releaseLock(); }
}

/* ---------- public API ---------- */
export const printerSupport = () => ({
  bluetooth: !!bluetooth(),
  usb: !!serial(),
  native: isNativeApp(),
});

/** Pick and connect a printer (needs a tap: browsers only show the device chooser from a user gesture). */
export async function connectPrinter(kind: PrinterKind, address?: string, name?: string) {
  usePrinter.setState({ busy: true, error: null });
  try {
    if (kind === 'bluetooth') {
      const bt = bluetooth();
      if (!bt) throw new Error('This browser has no Web Bluetooth. Use Chrome on Android or desktop Chrome / Edge, or the Android app.');
      const device = await bt.requestDevice({ acceptAllDevices: true, optionalServices: BLE_PRINTER_SERVICES });
      await openBle(device);
      savePrinter({ kind, name: device.name ?? 'Bluetooth printer', address: device.id });
    } else if (kind === 'usb') {
      const s = serial();
      if (!s) throw new Error('This browser has no Web Serial. Use desktop Chrome or Edge, or print with the system dialog.');
      const p = await s.requestPort();
      await p.open({ baudRate: 9600 }).catch(() => { /* already open */ });
      usbPort = p;
      const info = p.getInfo?.() ?? {};
      savePrinter({ kind, name: 'USB printer', usb: { vendor: info.usbVendorId, product: info.usbProductId } });
    } else if (kind === 'native-bt') {
      if (!address) throw new Error('Pick a paired printer.');
      const r = await SdDevices.connect({ id: 'printer', address }).catch((e) => { throw new Error(nativeMessage(e)); });
      savePrinter({ kind, name: r.name ?? name ?? address, address });
    } else {
      savePrinter({ kind: 'system', name: undefined, address: undefined });
    }
    usePrinter.setState({ connected: kind !== 'system' });
  } catch (e) {
    const msg = (e as Error).name === 'NotFoundError' ? 'No printer was picked.' : (e as Error).message;
    usePrinter.setState({ error: msg });
    throw new Error(msg);
  } finally {
    usePrinter.setState({ busy: false });
  }
}

export async function disconnectPrinter() {
  const { config } = usePrinter.getState();
  if (config.kind === 'bluetooth') ble?.device.gatt?.disconnect();
  if (config.kind === 'usb') await usbPort?.close().catch(() => {});
  if (config.kind === 'native-bt') await SdDevices.disconnect({ id: 'printer' }).catch(() => {});
  ble = null; usbPort = null;
  savePrinter({ kind: 'system', name: undefined, address: undefined, usb: undefined });
  usePrinter.setState({ connected: false, error: null });
}

/** Send raw ESC/POS bytes to the configured direct printer. */
export async function sendToPrinter(bytes: Uint8Array) {
  const { config } = usePrinter.getState();
  if (config.kind === 'bluetooth') await writeBle(bytes);
  else if (config.kind === 'usb') await writeUsb(bytes);
  else if (config.kind === 'native-bt') {
    const write = () => SdDevices.write({ id: 'printer', data: toBase64(bytes) });
    // After an app restart the link is closed: reconnect once to the saved printer.
    await write().catch(async () => {
      if (!config.address) throw new Error('Pick the Bluetooth printer again in Settings › Devices.');
      await SdDevices.connect({ id: 'printer', address: config.address });
      await write();
    }).catch((e) => { throw new Error(nativeMessage(e)); });
  } else throw new Error('No direct printer is set up.');
  usePrinter.setState({ connected: true, error: null });
}

export const directPrinter = () => usePrinter.getState().config.kind !== 'system';

/** Opens the cash drawer wired to the receipt printer (direct printers only). */
export async function openDrawer() {
  if (!directPrinter()) throw new Error('A cash drawer opens through a directly connected receipt printer.');
  await sendToPrinter(drawerKick());
}

/** Reconnect silently at start-up where the platform allows it (Android app, USB). Bluetooth LE reconnects on first print. */
export async function autoConnectPrinter() {
  const { config } = usePrinter.getState();
  try {
    if (config.kind === 'native-bt' && config.address && isNativeApp()) {
      await SdDevices.connect({ id: 'printer', address: config.address });
      usePrinter.setState({ connected: true });
    } else if (config.kind === 'usb') {
      const p = await findPort(config.usb);
      if (p) { await p.open({ baudRate: 9600 }).catch(() => {}); usbPort = p; usePrinter.setState({ connected: true }); }
    }
  } catch { /* printer off: the first print will say so */ }
}
